/**
 * The widget's hooks for Bluebeam Studio's two desktop halves (docs/BLUEBEAM-STUDIO.md sections 10.1 and 10.2), DESKTOP
 * SHELL ONLY: on the web and in the Office pane nothing here changes what the person sees.
 *  - The download relay: in the desktop shell a vault-download's "Download now" is a message to the native agent
 *    (`gyro:vault-download`), never an `<a download>` navigation, because the shell cancels every navigation off its page
 *    and the system browser cannot carry device custody (gyro-svc answers 401). The agent saves the file to Downloads and
 *    answers `gyro:vault-download-result`; "Open" sends only the hand-off id (`gyro:vault-open`), never a path.
 *  - The upload content leg: a confirmed `bluebeam_upload_file` answers with the Bridge's run in `applying` (gyro-svc waits
 *    two minutes for the bytes on PUT /v1/bluebeam/content/{runId}); the widget offers "Choose file and send", which asks
 *    the agent (`gyro:bluebeam-upload`) to pick, hash and stream the file natively, and shows the agent's answer
 *    (`gyro:bluebeam-upload-result`) in plain words.
 * The shell announces what it relays in `window.__gyroNativeFiles`; without it the widget keeps the web behaviour.
 */
export const BLUEBEAM_STUDIO_APP = 'bluebeam-studio';
export const BLUEBEAM_UPLOAD_TOOL = 'bluebeam_upload_file';
/** gyro-svc's content window (bluebeamStudio.ts BLUEBEAM_CONTENT_WINDOW_MS), in seconds. */
export const BLUEBEAM_CONTENT_WINDOW_S = 120;
/** The Bridge's transfer cap (bluebeamStudio.ts BLUEBEAM_UPLOAD_CAP_BYTES). */
export const BLUEBEAM_UPLOAD_CAP_BYTES = 100 * 1024 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHA256 = /^[a-f0-9]{64}$/;
const VAULT_URL = /^\/api\/vault\/download\/[A-Za-z0-9_-]{43}$/;
const clean = (value, max) => typeof value === 'string' && value.trim().length > 0 && value.length <= max && !/[\u0000-\u001f\u007f]/.test(value);
/** The shell's announcement, read field by field; a page without it (the web, the Office pane, an older agent) relays nothing. */
export function nativeFiles(win) {
    const value = win && typeof win === 'object' ? win.__gyroNativeFiles : undefined;
    const flags = value && typeof value === 'object' ? value : {};
    return { vaultDownload: flags.vaultDownload === true, bluebeamUpload: flags.bluebeamUpload === true, autoSave: flags.autoSave === true };
}
/**
 * The content leg a confirm's answer (or the inbox's re-report) opened, or null for any other action or answer. The
 * answer is the governed envelope with `run.status === 'applying'` (POST /v1/action answers it as a 409 `status: 'error'`
 * with `reconciliationRequired`, which for this one tool means "the bytes are owed", not "reconcile"); the facts come
 * from the action's own input, the same values the person approved on the card.
 */
export function bluebeamUploadLeg(action, response, now = Date.now()) {
    if (action.tool !== BLUEBEAM_UPLOAD_TOOL || action.app !== BLUEBEAM_STUDIO_APP || !action.input || typeof action.input !== 'object')
        return null;
    if (!response || typeof response !== 'object')
        return null;
    const root = response;
    const run = root.run && typeof root.run === 'object' && !Array.isArray(root.run) ? root.run : null;
    if (!run || run.status !== 'applying' || typeof run.id !== 'string' || !UUID.test(run.id))
        return null;
    const { fileName, sizeBytes, sha256, kind } = action.input;
    if (!clean(fileName, 255) || !Number.isSafeInteger(sizeBytes) || Number(sizeBytes) < 1 || Number(sizeBytes) > BLUEBEAM_UPLOAD_CAP_BYTES)
        return null;
    if (typeof sha256 !== 'string' || !SHA256.test(sha256) || (kind !== 'session' && kind !== 'project'))
        return null;
    return { runId: run.id.toLowerCase(), fileName, sizeBytes: Number(sizeBytes), sha256, kind, expiresAt: Math.floor(now / 1000) + BLUEBEAM_CONTENT_WINDOW_S };
}
const UPLOAD_STATUSES = new Set(['picking', 'sending', 'sent', 'refused', 'failed', 'cancelled']);
/** The agent's answer on a content leg (`gyro:bluebeam-upload-result`), validated field by field. */
export function parseBluebeamUploadResult(detail) {
    if (!detail || typeof detail !== 'object')
        return null;
    const { runId, status, message } = detail;
    if (typeof runId !== 'string' || !UUID.test(runId) || typeof status !== 'string' || !UPLOAD_STATUSES.has(status))
        return null;
    if (message !== undefined && !clean(message, 1000))
        return null;
    return { runId: runId.toLowerCase(), status: status, ...(message ? { message } : {}) };
}
/** The line under the upload's card for the leg's state. */
export function bluebeamUploadWords(result, leg) {
    switch (result?.status) {
        case 'picking': return `Choose "${leg.fileName}" on this PC in the dialog.`;
        case 'sending': return `Sending "${leg.fileName}" to Bluebeam Studio…`;
        case 'sent': return result.message ?? 'Sent to Bluebeam Studio. Everyone in it can see the file now.';
        case 'refused':
        case 'failed':
        case 'cancelled': return result.message ?? 'Nothing was sent to Bluebeam Studio. Start the upload again.';
        default: return `Confirmed. Choose "${leg.fileName}" on this PC to send it; Bluebeam Studio takes it within two minutes of your confirmation.`;
    }
}
/** Whether the leg still offers its button: not sent, not refused, and gyro-svc's window not over. */
export function bluebeamUploadOpen(result, leg, now = Date.now()) {
    if (Math.floor(now / 1000) >= leg.expiresAt)
        return false;
    return result === undefined || result.status === 'cancelled' || result.status === 'failed';
}
const VAULT_STATUSES = new Set(['saved', 'failed', 'opened', 'open_failed']);
/** The agent's answer on a vault download or an open (`gyro:vault-download-result`), validated field by field. */
export function parseVaultDownloadResult(detail) {
    if (!detail || typeof detail !== 'object')
        return null;
    const { id, status, fileName, folder, message } = detail;
    if (!clean(id, 200) || typeof status !== 'string' || !VAULT_STATUSES.has(status))
        return null;
    if ((fileName !== undefined && !clean(fileName, 260)) || (folder !== undefined && !clean(folder, 100)) || (message !== undefined && !clean(message, 1000)))
        return null;
    return { id, status: status, ...(fileName ? { fileName } : {}), ...(folder ? { folder } : {}), ...(message ? { message } : {}) };
}
/** Whether a vault-download hand-off URL is one the shell relays (the relative route; an absolute one stays a link). */
export function relayableVaultUrl(url) {
    return VAULT_URL.test(url);
}
export function vaultDownloadView(result, requested) {
    if (!result)
        return requested === 'download' ? { state: 'saving' } : { state: 'ready' };
    switch (result.status) {
        case 'saved': return { state: 'saved', fileName: result.fileName ?? 'file', folder: result.folder ?? 'Downloads', opening: requested === 'open' };
        case 'opened': return { state: 'saved', fileName: result.fileName ?? 'file', folder: result.folder ?? 'Downloads', opening: false };
        case 'open_failed': return { state: 'saved', fileName: result.fileName ?? 'file', folder: result.folder ?? 'Downloads', opening: false, note: result.message ?? 'Windows could not open it. Find it in your Downloads folder.' };
        case 'failed': return { state: 'failed', message: result.message ?? 'The file could not be brought over. Ask Gyro for it again.' };
    }
}
//# sourceMappingURL=bluebeamStudio.js.map